// AVALIAÇÃO DA LIGAÇÃO COM IA: espelha a feature `ai-call-eval` do back. No
// wrap-up do discador em modo "IA atende", o vendedor dá nota à voz e à
// coerência da IA, marca se precisou assumir (e por quê) e deixa um comentário
// (POST /v1/calls/:id/ai-evaluation). A gestão lê as médias e o % de assumidos
// (GET /v1/ai-call-eval e /v1/ai-call-eval/summary).
//
// A ESCRITA e a LEITURA moram no mesmo arquivo mesmo tendo telas e papéis
// diferentes porque são o mesmo registro por dois lados: `AiEvaluationPayload`
// (o que o vendedor manda), `AiEvaluationListItem` (o que a gestão vê) e
// `AiEvalSummary` (a agregação) compartilham o `TakeoverReason` — a união de
// cinco strings que é o conteúdo real deste domínio.
//
// Fica de fora o formulário pós-ligação (./call-form.js): também é wrap-up, mas
// é definido pela org, vale para TODA chamada e tem outra rota. Aqui os campos
// são fixos e a ligação precisa ter sido conduzida pela IA (o back recusa o
// resto com `not_ai_call`).
import { api, toApiError } from "./client.js";

export const TAKEOVER_REASONS = [
  "ia_travou",
  "ia_errou",
  "lead_pediu_humano",
  "lead_irritado",
  "outro",
] as const;
export type TakeoverReason = (typeof TAKEOVER_REASONS)[number];

export const TAKEOVER_REASON_LABELS: Record<TakeoverReason, string> = {
  ia_travou: "IA travou / não respondeu",
  ia_errou: "IA respondeu errado",
  lead_pediu_humano: "Lead pediu humano",
  lead_irritado: "Lead irritado / desconfiou",
  outro: "Outro",
};

export interface AiEvaluationPayload {
  voiceRating: number; // 1–5
  coherenceRating: number; // 1–5
  tookOver: boolean;
  takeoverReason?: TakeoverReason; // obrigatório sse tookOver
  comment?: string;
}

export interface AiEvaluationListItem {
  id: string;
  callId: string;
  voiceRating: number;
  coherenceRating: number;
  tookOver: boolean;
  takeoverReason: TakeoverReason | null;
  comment: string | null;
  createdAt: string;
  evaluatedBy: string;
  agentName: string | null;
  targetPhone: string | null;
  callCreatedAt: string | null;
}

export interface AiEvalSummary {
  count: number;
  avgVoice: number | null;
  avgCoherence: number | null;
  takeoverCount: number;
  takeoverRate: number; // 0..1
  byTakeoverReason: { reason: TakeoverReason; count: number }[];
}

// POST /v1/calls/:id/ai-evaluation — grava a avaliação da ligação de IA.
export async function submitAiCallEvaluation(
  callId: string,
  payload: AiEvaluationPayload,
): Promise<{ submitted: true }> {
  const { data, error } = await api<{ submitted: true }>(`/v1/calls/${callId}/ai-evaluation`, {
    method: "POST",
    body: payload,
  });
  if (error) throw toApiError(`POST /v1/calls/${callId}/ai-evaluation`, error);
  return data;
}

// GET /v1/ai-call-eval — lista as avaliações do período (gestão).
export async function listAiCallEvaluations(
  params: { from?: string; to?: string; limit?: number } = {},
): Promise<AiEvaluationListItem[]> {
  const { data, error } = await api<{ evaluations: AiEvaluationListItem[] }>("/v1/ai-call-eval", {
    query: params,
  });
  if (error) throw toApiError("GET /v1/ai-call-eval", error);
  return data.evaluations;
}

// GET /v1/ai-call-eval/summary — agregado do período (gestão).
export async function getAiEvalSummary(
  params: { from?: string; to?: string } = {},
): Promise<AiEvalSummary> {
  const { data, error } = await api<AiEvalSummary>("/v1/ai-call-eval/summary", {
    query: params,
  });
  if (error) throw toApiError("GET /v1/ai-call-eval/summary", error);
  return data;
}
